import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Row,
  Col,
  Upload,
  Avatar,
  Tabs,
  Select,
  DatePicker,
  Radio
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  SafetyOutlined,
  UploadOutlined,
  EditOutlined,
  GlobalOutlined,
  EnvironmentOutlined,
  HeartOutlined,
  FormOutlined
} from '@ant-design/icons';
import { userAPI, profileAPI, IMG_URL } from '../utils/api';
import { useAuth } from '../hooks/useAuthHook';
import { validateNickname, validateTextField, validateWebsite, validateDescription, validatePassword } from '../utils/validator';
import useDocumentTitle from '../hooks/useDocumentTitle';

const { TabPane } = Tabs;
const { Option } = Select;

const IdenManagement = () => {
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [phoneForm] = Form.useForm();
  const [nicknameForm] = Form.useForm();

  // 设置网页标题
  useDocumentTitle('个人中心', {
    prefix: '',
    suffix: ' - rtycn'
  });

  useEffect(() => {
    if (user) {
      fetchProfile();
      profileForm.setFieldsValue(user);
    }
  }, [user]);

  const fetchProfile = async () => {
    try {
      setProfileLoading(true);
      const response = await profileAPI.getProfile(user.username);
      const profileData = response.data || {};

      // 转换日期格式
      if (profileData.birthday) {
        profileData.birthday = formatBirthday(profileData.birthday);
      }

      setProfile(response.data);
      profileForm.setFieldsValue(profileData);
    } catch (error) {
      console.error('获取个人信息失败:', error);
      message.error('获取个人信息失败，请刷新页面重试');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleProfileUpdate = async (values) => {
    try {
      // 前端验证
      if (values.nickname) {
        const nicknameResult = validateNickname(values.nickname);
        if (!nicknameResult.valid) {
          message.error(nicknameResult.message);
          return;
        }
      }

      if (values.website) {
        const websiteResult = validateWebsite(values.website);
        if (!websiteResult.valid) {
          message.error(websiteResult.message);
          return;
        }
      }

      if (values.location) {
        const locationResult = validateTextField(values.location, { fieldName: '所在地', max: 100 });
        if (!locationResult.valid) {
          message.error(locationResult.message);
          return;
        }
      }

      if (values.signature) {
        const signatureResult = validateDescription(values.signature, { fieldName: '个性签名', max: 100 });
        if (!signatureResult.valid) {
          message.error(signatureResult.message);
          return;
        }
      }

      if (values.interests) {
        const interestsResult = validateTextField(values.interests, { fieldName: '兴趣爱好', max: 200 });
        if (!interestsResult.valid) {
          message.error(interestsResult.message);
          return;
        }
      }

      if (values.bio) {
        const bioResult = validateDescription(values.bio, { fieldName: '个人简介', max: 500 });
        if (!bioResult.valid) {
          message.error(bioResult.message);
          return;
        }
      }

      setLoading(true);

      // 转换日期格式
      const formData = { ...values };
      if (formData.birthday) {
        formData.birthday = formData.birthday.format('YYYY-MM-DD');
      }

      await profileAPI.updateProfile(user.username, formData);
      message.success('个人信息更新成功');
      fetchProfile();
    } catch (error) {
      console.error('更新个人信息失败:', error);
      message.error('更新个人信息失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 将后端返回的生日日期字符串转换为 dayjs 对象
  const formatBirthday = (birthday) => {
    if (!birthday) return null;
    return dayjs(birthday);
  };

  const handlePasswordChange = async (values) => {
    try {
      // 前端验证
      const newPasswordResult = validatePassword(values.newPassword);
      if (!newPasswordResult.valid) {
        message.error(newPasswordResult.message);
        return;
      }

      setLoading(true);
      await userAPI.changePassword(user.username, values.newPassword);
      message.success('密码修改成功，请重新登录');
      passwordForm.resetFields();

      // 密码修改后重新登录
      setTimeout(() => {
        logout();
      }, 1000);
    } catch (error) {
      console.error('密码修改失败:', error);
      message.error('密码修改失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = async (values) => {
    try {
      setLoading(true);
      await userAPI.updateEmail(user.username, values.newEmail);
      message.success('邮箱修改成功，请重新登录');
      emailForm.resetFields();

      setTimeout(() => {
        logout();
      }, 1000);
    } catch (error) {
      console.error('邮箱修改失败:', error);
      message.error('邮箱修改失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneChange = async (values) => {
    try {
      setLoading(true);
      await userAPI.updatePhone(user.username, values.newPhone);
      message.success('手机号修改成功');
      phoneForm.resetFields();

      // 更新本地用户信息中的手机号
      if (user) {
        user.phone = values.newPhone;
        // 同时更新localStorage中的用户信息
        const currentUser = JSON.parse(localStorage.getItem('current_user') || 'null');
        if (currentUser) {
          currentUser.phone = values.newPhone;
          localStorage.setItem('current_user', JSON.stringify(currentUser));
        }
      }
      if (profile) {
        setProfile({ ...profile, phone: values.newPhone });
      }

      fetchProfile();
    } catch (error) {
      console.error('手机号修改失败:', error);
      message.error('手机号修改失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const handleNicknameChange = async (values) => {
    try {
      // 前端验证
      const nicknameResult = validateNickname(values.newNickname);
      if (!nicknameResult.valid) {
        message.error(nicknameResult.message);
        return;
      }

      setLoading(true);
      await userAPI.updateNickname(user.username, values.newNickname);
      message.success('昵称修改成功');
      nicknameForm.resetFields();
      fetchProfile();
    } catch (error) {
      console.error('昵称修改失败:', error);
      message.error('昵称修改失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      setAvatarUploading(true);
      const response = await profileAPI.uploadAvatar(user.username, formData);
      message.success('头像上传成功');
      fetchProfile();
      return false; // 阻止默认上传行为
    } catch (error) {
      console.error('头像上传失败:', error);
      message.error('头像上传失败，请稍后重试');
      return false;
    } finally {
      setAvatarUploading(false);
    }
  };

  const uploadProps = {
    beforeUpload: handleAvatarUpload,
    showUploadList: false,
    accept: 'image/*',
  };

  return (
    <div style={{ width: '100%', maxWidth: 800, margin: '0 auto', paddingTop: 24 }}>
      <Card>
        <Tabs defaultActiveKey="1">
          <TabPane
            tab={
              <span>
                <UserOutlined /> 个人信息
              </span>
            }
            key="1"
          >
            <div style={{ textAlign: 'center', marginTop: 6 }}>
              <Upload {...uploadProps}>
                <div style={{ cursor: 'pointer', display: 'inline-block' }}>
                  <Avatar
                    size={100}
                    src={profile?.avatar ? `${IMG_URL}${profile?.avatar}` : user?.avatar ? `${IMG_URL}${user?.avatar}` : undefined}
                    icon={<UserOutlined />}
                    style={{ marginBottom: 8 }}
                  />
                  <div>
                    <Button
                      icon={<UploadOutlined />}
                      loading={avatarUploading}
                      size="small"
                    >
                      更换头像
                    </Button>
                  </div>
                </div>
              </Upload>
            </div>

            <Form
              form={profileForm}
              layout="vertical"
              onFinish={handleProfileUpdate}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="username"
                    label="用户名"
                  >
                    <Input prefix={<UserOutlined />} disabled />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="nickname"
                    label="昵称"
                    rules={[{ required: true, message: '请输入昵称' }]}
                  >
                    <Input prefix={<EditOutlined />} placeholder="请输入昵称" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="gender"
                    label="性别"
                  >
                    <Radio.Group>
                      <Radio value="male">男</Radio>
                      <Radio value="female">女</Radio>
                      <Radio value="other">其他</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="birthday"
                    label="生日"
                  >
                    <DatePicker style={{ width: '100%' }} placeholder="请选择生日" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="website"
                    label="个人网站"
                  >
                    <Input prefix={<GlobalOutlined />} placeholder="请输入个人网站" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="location"
                    label="所在地"
                  >
                    <Input prefix={<EnvironmentOutlined />} placeholder="请输入所在地" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="signature"
                label="个性签名"
              >
                <Input prefix={<FormOutlined />} placeholder="请输入个性签名" />
              </Form.Item>

              <Form.Item
                name="interests"
                label="兴趣爱好"
              >
                <Input prefix={<HeartOutlined />} placeholder="请输入兴趣爱好，多个用逗号分隔" />
              </Form.Item>

              <Form.Item
                name="bio"
                label="个人简介"
              >
                <Input.TextArea
                  rows={4}
                  placeholder="请输入个人简介"
                />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} block>
                  保存个人信息
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane
            tab={
              <span>
                <EditOutlined /> 修改昵称
              </span>
            }
            key="4"
          >
            <Form
              form={nicknameForm}
              layout="vertical"
              onFinish={handleNicknameChange}
            >
              <Form.Item
                name="newNickname"
                label="新昵称"
                rules={[
                  { required: true, message: '请输入新昵称' },
                  { min: 2, message: '昵称长度至少2位' },
                  { max: 20, message: '昵称长度最多20位' }
                ]}
              >
                <Input prefix={<EditOutlined />} placeholder="请输入新昵称" />
              </Form.Item>

              <Form.Item
                name="confirmNickname"
                label="确认昵称"
                dependencies={['newNickname']}
                rules={[
                  { required: true, message: '请确认昵称' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newNickname') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('两次输入的昵称不一致'));
                    },
                  }),
                ]}
              >
                <Input prefix={<EditOutlined />} placeholder="请再次输入新昵称" />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} block>
                  修改昵称
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane
            tab={
              <span>
                <GlobalOutlined /> 修改邮箱
              </span>
            }
            key="5"
          >
            <Form
              form={emailForm}
              layout="vertical"
              onFinish={handleEmailChange}
            >
              <Form.Item label="当前邮箱">
                <Input disabled value={user?.email} />
              </Form.Item>

              <Form.Item
                name="newEmail"
                label="新邮箱"
                rules={[
                  { required: true, message: '请输入新邮箱' },
                  { type: 'email', message: '请输入有效的邮箱地址' }
                ]}
              >
                <Input prefix={<GlobalOutlined />} placeholder="请输入新邮箱" />
              </Form.Item>

              <Form.Item
                name="confirmEmail"
                label="确认邮箱"
                dependencies={['newEmail']}
                rules={[
                  { required: true, message: '请确认邮箱' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newEmail') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('两次输入的邮箱不一致'));
                    },
                  }),
                ]}
              >
                <Input prefix={<GlobalOutlined />} placeholder="请再次输入新邮箱" />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} block>
                  修改邮箱
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane
            tab={
              <span>
                <SafetyOutlined /> 修改手机号
              </span>
            }
            key="6"
          >
            <Form
              form={phoneForm}
              layout="vertical"
              onFinish={handlePhoneChange}
            >
              <Form.Item label="当前手机号">
                <Input disabled value={user?.phone || profile?.phone || '未设置'} />
              </Form.Item>

              <Form.Item
                name="newPhone"
                label="新手机号"
                rules={[
                  { required: true, message: '请输入新手机号' },
                  { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号' }
                ]}
              >
                <Input prefix={<SafetyOutlined />} placeholder="请输入新手机号" />
              </Form.Item>

              <Form.Item
                name="confirmPhone"
                label="确认手机号"
                dependencies={['newPhone']}
                rules={[
                  { required: true, message: '请确认手机号' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPhone') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('两次输入的手机号不一致'));
                    },
                  }),
                ]}
              >
                <Input prefix={<SafetyOutlined />} placeholder="请再次输入新手机号" />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} block>
                  修改手机号
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane
            tab={
              <span>
                <LockOutlined /> 密码修改
              </span>
            }
            key="2"
          >
            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={handlePasswordChange}
            >
              <Form.Item
                name="currentPassword"
                label="当前密码"
                rules={[{ required: true, message: '请输入当前密码' }]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="请输入当前密码"
                />
              </Form.Item>

              <Form.Item
                name="newPassword"
                label="新密码"
                rules={[
                  { required: true, message: '请输入新密码' },
                  { min: 6, message: '密码长度至少6位' }
                ]}
              >
                <Input.Password
                  prefix={<SafetyOutlined />}
                  placeholder="请输入新密码"
                />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="确认新密码"
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: '请确认新密码' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('两次输入的密码不一致'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<SafetyOutlined />}
                  placeholder="请确认新密码"
                />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} block>
                  修改密码
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane
            tab={
              <span>
                <SafetyOutlined /> 安全提醒
              </span>
            }
            key="3"
          >
            <div style={{ color: '#666', lineHeight: 1.6, padding: '20px 0' }}>
              <p>• 请定期修改密码，确保账户安全</p>
              <p>• 建议使用包含数字、字母和特殊字符的复杂密码</p>
              <p>• 不要在公共场所或不安全的网络环境下登录</p>
              <p>• 如发现异常登录行为，请及时修改密码</p>
            </div>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default IdenManagement;